/**
 * Kiểm tra logic bộ câu hỏi (port của `lintForm()` trong bản mẫu, ~dòng 1666).
 *
 * Đây là kiểm tra **cấu trúc form**, khác hẳn audit dữ liệu (`@farmer/audit-rules`):
 * linter đọc schema và trả về lỗi sẽ khiến câu hỏi không bao giờ hiện, tham chiếu
 * biến sai phạm vi, hay tổng không có ràng buộc. Nó không nhìn `answers`.
 *
 * Dùng ở hai chỗ: bảng "Kiểm tra logic" trên phiếu (chỉ đọc) và Question Builder
 * (chặn phát hành phiên bản mới khi còn lỗi `error`).
 */

import { expressionProblem, isDynamicList, KNOWN_DYNAMIC_LISTS, MASTER_DATA_KEYS } from '@farmer/audit-rules';

/** Cột mà engine đánh giá như biểu thức — xem `expressionProblem` trong @farmer/audit-rules. */
const EXPRESSION_COLUMNS = ['relevant', 'calculation', 'repeat_count', 'constraint', 'choice_filter', 'default'];

const REF_RE = /\$\{\s*([\w\- ]+)\s*\}/g;
const AGG_RE = /\b(?:sum|count)\(\s*\$\{\s*([\w\- ]+)\s*\}\s*\)/g;
const REL_RE = /^\s*\$\{([\w\- ]+)\}\s*(=|!=|>=|<=|>|<)\s*'?"?([^'"\s]+)'?"?\s*$/;
const EXPR_COLUMNS = ['label', 'hint', 'relevant', 'calculation', 'constraint', 'repeat_count', 'choice_filter', 'trigger', 'ui_row_label'];

function indexChoices(list) {
  const map = {};
  (list || []).forEach((c) => {
    const k = c['list name'];
    if (k) (map[k] = map[k] || []).push(c);
  });
  return map;
}

/**
 * @param {{survey: any[], choices: any[]}} schema
 * @param {{formKey?: string}} [opts] - có `formKey` thì khoá chỉ đọc của master data
 *   (`MASTER_DATA_KEYS`, vd `${hh_province}` của phiếu RCP) được coi là biến hợp lệ ở
 *   phạm vi gốc — chúng không là câu hỏi nhưng nhãn và `relevant` đọc được (R1).
 * @returns {Array<{sev:'error'|'warn', row:string, col:string, msg:string}>}
 */
export function lintForm(schema, opts = {}) {
  const rows = (schema?.survey || []).filter((r) => String(r.type || '').trim());
  const choices = indexChoices(schema?.choices);
  const issues = [];

  // scopeOf: tên biến -> đường dẫn nhóm lặp bao ngoài ('' = phạm vi gốc)
  const scopeOf = {};
  (MASTER_DATA_KEYS[opts.formKey] || []).forEach((k) => {
    scopeOf[k] = '';
  });
  const defs = [];
  const stack = [];
  rows.forEach((r) => {
    const t = String(r.type).trim();
    if (t === 'begin repeat') {
      scopeOf[r.name] = stack.join('/');
      stack.push(r.name);
      return;
    }
    if (t === 'begin group' || t === 'end group') return;
    if (t === 'end repeat') {
      stack.pop();
      return;
    }
    scopeOf[r.name] = stack.join('/');
    defs.push({ ...r, scope: stack.join('/') });
  });

  const inScope = (refScope, defScope) =>
    defScope === '' || refScope === defScope || refScope.startsWith(defScope + '/');

  // 1. Tham chiếu ${...} không tồn tại hoặc nằm ngoài phạm vi
  const refStack = [];
  rows.forEach((r) => {
    const t = String(r.type).trim();
    const cur = refStack.join('/');
    EXPR_COLUMNS.forEach((col) => {
      const txt = String(r[col] || '');
      const found = txt.match(REF_RE) || [];
      // `sum(${x})` / `count(${x})` gom một cột của bảng lặp — đọc từ ngoài bảng là đúng mục đích.
      const aggregated = new Set([...txt.matchAll(AGG_RE)].map((m) => m[1].trim()));
      found.forEach((tok) => {
        const nm = tok.slice(2, -1).trim();
        if (!(nm in scopeOf)) {
          issues.push({
            sev: 'error',
            row: r.name || t,
            col,
            msg: `Tham chiếu \${${nm}} không tồn tại trong bộ câu hỏi.`,
          });
        } else if (!inScope(cur, scopeOf[nm]) && !aggregated.has(nm)) {
          issues.push({
            sev: 'error',
            row: r.name || t,
            col,
            msg: `\${${nm}} nằm ngoài phạm vi (khai báo trong nhóm lặp "${scopeOf[nm] || 'gốc'}") → khi chạy sẽ luôn rỗng.`,
          });
        }
      });
    });
    if (t === 'begin repeat') refStack.push(r.name);
    if (t === 'end repeat') refStack.pop();
  });

  // 2. relevant so sánh với giá trị không có trong danh sách lựa chọn
  //    → câu hỏi không bao giờ hiện ra.
  defs.forEach((r) => {
    const m = String(r.relevant || '').match(REL_RE);
    if (!m || m[2] !== '=') return;
    const src = defs.find((d) => d.name === m[1].trim());
    if (!src) return;
    const lm = String(src.calculation || '').match(
      /^instance\('([\w-]+)'\)\/root\/item\[name=\$\{[\w-]+\}\]\/(\w+)$/,
    );
    if (!lm) return;
    const vals = (choices[lm[1]] || []).map((x) => String(x[lm[2]] ?? ''));
    if (!vals.includes(m[3])) {
      issues.push({
        sev: 'error',
        row: r.name,
        col: 'relevant',
        msg: `So sánh với '${m[3]}' nhưng danh sách "${lm[1]}" không có giá trị ${lm[2]} nào như vậy — câu hỏi sẽ không bao giờ hiện.`,
      });
    }
  });

  // 3. calculate là tổng của nhiều trường nhưng không có constraint ép tổng
  defs.forEach((r) => {
    const c = String(r.calculation || '');
    const refCount = (c.match(/\$\{/g) || []).length;
    if (refCount >= 3 && c.includes('+') && !r.constraint) {
      issues.push({
        sev: 'warn',
        row: r.name,
        col: 'constraint',
        msg: `Là tổng của ${refCount} trường nhưng không có constraint ép tổng — người nhập có thể để lệch.`,
      });
    }
  });

  // 4. Tên biến chứa khoảng trắng — vỡ khi export/ETL
  defs.forEach((r) => {
    if (/\s/.test(r.name || '')) {
      issues.push({ sev: 'warn', row: r.name, col: 'name', msg: 'Tên biến chứa khoảng trắng — dễ vỡ khi xuất dữ liệu.' });
    }
  });

  // 5. Tên biến trùng nhau — `answers` là map phẳng nên bản ghi sau đè bản trước
  const seen = new Set();
  defs.forEach((r) => {
    if (!r.name) return;
    if (seen.has(r.name)) {
      issues.push({ sev: 'error', row: r.name, col: 'name', msg: 'Tên biến bị trùng — câu trả lời sẽ ghi đè lẫn nhau.' });
    }
    seen.add(r.name);
  });

  // 6. select_one/select_multiple trỏ tới danh sách không tồn tại. Danh sách động
  //    (`__middlemen`) nạp từ master data lúc chạy — không có trong sheet choices là đúng;
  //    tên `__` lạ thì không ai nạp nó, câu hỏi sẽ không có lựa chọn nào.
  defs.forEach((r) => {
    const m = String(r.type || '').match(/^select_(one|multiple)\s+(\S+)/);
    if (!m) return;
    if (isDynamicList(m[2])) {
      if (!KNOWN_DYNAMIC_LISTS.has(m[2])) {
        issues.push({
          sev: 'error',
          row: r.name,
          col: 'type',
          msg: `Danh sách động "${m[2]}" không được hỗ trợ — chỉ có: ${[...KNOWN_DYNAMIC_LISTS].join(', ')}.`,
        });
      }
      return;
    }
    if (!choices[m[2]] || !choices[m[2]].length) {
      issues.push({
        sev: 'error',
        row: r.name,
        col: 'type',
        msg: `Danh sách lựa chọn "${m[2]}" không có dòng nào trong sheet choices.`,
      });
    }
  });

  // 7. Cặp begin/end không cân — buildTree sẽ gộp nhầm câu hỏi vào nhóm khác
  let depth = 0;
  rows.forEach((r) => {
    const t = String(r.type).trim();
    if (t === 'begin group' || t === 'begin repeat') depth++;
    if (t === 'end group' || t === 'end repeat') {
      depth--;
      if (depth < 0) {
        issues.push({ sev: 'error', row: t, col: 'type', msg: 'Có "end" thừa, không khớp "begin" nào.' });
        depth = 0;
      }
    }
  });
  if (depth > 0) {
    issues.push({ sev: 'error', row: '(cuối form)', col: 'type', msg: `Còn ${depth} nhóm chưa được đóng bằng "end group"/"end repeat".` });
  }

  // 8. Biểu thức hỏng cú pháp. Lúc chạy engine nuốt lỗi (ra rỗng / hiện câu) để phiếu
  //    không vỡ giữa vườn — nên phải chặn ở lúc soạn. Xét theo đúng đường engine chọn:
  //    biểu thức thuộc tập cũ không bao giờ bị báo.
  rows.forEach((r) => {
    EXPRESSION_COLUMNS.forEach((col) => {
      const problem = expressionProblem(col, r[col]);
      if (problem) {
        issues.push({ sev: 'error', row: r.name || String(r.type).trim(), col, msg: `Biểu thức lỗi: ${problem}` });
      }
    });
    // Tên dòng của bảng lặp được tính như một `calculation`.
    const rowLabelProblem = expressionProblem('calculation', r.ui_row_label);
    if (rowLabelProblem) {
      issues.push({ sev: 'error', row: r.name, col: 'ui_row_label', msg: `Biểu thức lỗi: ${rowLabelProblem}` });
    }
  });

  // 10. `ui_max_rows`: số nguyên dương, chỉ trên bảng lặp nhập tay (bảng tự động thì số dòng do phiếu quyết).
  rows.forEach((r) => {
    const raw = String(r.ui_max_rows ?? '').trim();
    if (!raw) return;
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0) {
      issues.push({ sev: 'error', row: r.name, col: 'ui_max_rows', msg: `ui_max_rows phải là số nguyên dương, đang là "${raw}".` });
    } else if (String(r.type).trim() !== 'begin repeat' || String(r.repeat_count || '').trim() || String(r.ui_fixed_list || '').trim()) {
      issues.push({ sev: 'warn', row: r.name, col: 'ui_max_rows', msg: 'ui_max_rows chỉ có tác dụng trên bảng lặp nhập tay (không repeat_count, không ui_fixed_list).' });
    }
  });

  // 9. Bảng dòng cố định (`ui_fixed_list`) trỏ tới danh sách rỗng → bảng không có dòng nào.
  rows.forEach((r) => {
    const list = String(r.ui_fixed_list || '').trim();
    if (!list) return;
    if (String(r.type).trim() !== 'begin repeat') {
      issues.push({ sev: 'warn', row: r.name, col: 'ui_fixed_list', msg: 'ui_fixed_list chỉ có tác dụng trên "begin repeat".' });
    } else if (!choices[list] || !choices[list].length) {
      issues.push({
        sev: 'error',
        row: r.name,
        col: 'ui_fixed_list',
        msg: `Danh sách "${list}" không có dòng nào trong sheet choices — bảng sẽ không có dòng nào.`,
      });
    }
  });

  return issues;
}

export function lintSummary(issues) {
  const errors = issues.filter((i) => i.sev === 'error').length;
  return { errors, warnings: issues.length - errors, total: issues.length };
}
